// Copyright (c) Microsoft. All rights reserved.

import {
    Caption1,
    Card,
    CardHeader,
    ProgressBar,
    Text,
    makeStyles,
    mergeClasses,
    shorthands,
    tokens,
} from '@fluentui/react-components';
import React from 'react';
import { customColors, customFonts } from '../../../styles';
import { getFileIconByFileExtension } from '../../../features/Documents/components/DocumentsTab';
import { ChatMessage } from '../../../domain/messages/models/ChatMessage';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.margin(tokens.spacingVerticalXXS, 0),
    },
    card: {
        height: 'fit-content',
        width: '275px',
        backgroundColor: tokens.colorNeutralBackground3,
        ...shorthands.gap(0),
        ...shorthands.margin(tokens.spacingVerticalXXS, 0),
        padding: '2px 0px 0px 0px'
    },
    cardCaption: {
        color: tokens.colorNeutralForeground2,
        marginBottom: '5px'
    },
    cardHeader: {
        ...shorthands.margin(0, tokens.spacingHorizontalS),
    },
    cardHeaderText: {
        fontSize: 'small',
        fontWeight: '500',
        fontFamily: customFonts.Lato,
    },
    footer: {
        float: 'right',
        fontSize: 'small',
        fontWeight: '500',
        fontFamily: customFonts.Lato,
        color: tokens.colorNeutralForeground2,
        marginTop: '3px'
    },

    icon: {
        height: '32px',
        width: '32px',
    },
    floatLeft: {
        float: 'left',
    },
    button: {
        ...shorthands.padding('5px'),
        ...shorthands.borderRadius('5px'),
        fontSize: 'small',
        fontWeight: '500',
        fontFamily: customFonts.Lato,
        width: '100px',
        ...shorthands.margin('3px'),
        textAlign: 'center',
        backgroundColor: customColors.summarizeButtonDarker,
        ...shorthands.border(0),
        color: customColors.white,
        '&:hover': {
            backgroundColor: customColors.newChatHoverLightBlue,
            cursor: 'pointer',
        },
    },
    summarize: {
        paddingTop: '10px',
        alignSelf: 'end',
    },
    progressBarCustomStyles: {
        '& .fui-ProgressBar__bar': {
            backgroundColor: customColors.protivitiOrange,
            borderTopLeftRadius: '0px',    
            borderBottomLeftRadius: '0px', 
            borderTopRightRadius: '16px', 
            borderBottomRightRadius: '16px',
        },
        '& .fui-ProgressBar__track': {
            borderTopLeftRadius: '0px',
            borderBottomLeftRadius: '0px',
            borderTopRightRadius: '16px',
            borderBottomRightRadius: '16px',
        },
    }
});

interface ChatHistoryDocumentContentProps {
    isUser: boolean;
    message: ChatMessage;
}

interface FileStatusEntry {
    status: string;
    details?: string;
}

interface DocumentData {
    name: string;
    size: string;
    isUploaded: boolean;
    isProgressMessage?: boolean;
    uploadPercentage?: number;
    fileStatus?: FileStatusEntry[];
}

interface DocumentMessageContent {
    documents: DocumentData[];
}

export const ChatHistoryDocumentContent: React.FC<ChatHistoryDocumentContentProps> = ({ isUser, message }) => {
    const classes = useClasses();
    const parsedData = JSON.parse(message.content);
    let formattedContent = parsedData.content;
    if (typeof formattedContent === 'string') {
        try {
        formattedContent = JSON.parse(formattedContent);
        } catch (error) {
            console.error('Error parsing formatted content: ', error);
        }
    }
    const finalFormattedContent = JSON.stringify(formattedContent);

    let documents: DocumentData[] = [];
    try {
        if (parsedData.content){
            ({ documents } = JSON.parse(finalFormattedContent) as DocumentMessageContent);
        }
        else{
            ({ documents } = JSON.parse(message.content) as DocumentMessageContent);
        }
    } catch (e) {
        console.error('Error parsing chat history file item: ' + message.content);
    }

    return (
        <>
            {documents.map((document, index) => {
                const isUploading = document.isProgressMessage && document.uploadPercentage != undefined && document.uploadPercentage != 1;

                // Determine success from fileStatus steps when available; fall back to isUploaded
                const isSuccess = (() => {
                    if (document.fileStatus && document.fileStatus.length > 0) {
                        return document.fileStatus.every((fs) => fs.status === 'Success');
                    }
                    return document.isUploaded;
                })();

                return (
                
                    <div className={classes.root} key={`${message.id ?? 'unknown-message-id'}-document-${index}`}>
                        <Card appearance="filled-alternative" className={classes.card}>
                            <CardHeader
                                className={classes.cardHeader}
                                image={getFileIconByFileExtension(document.name, { className: classes.icon })}
                                header={<Text className={classes.cardHeaderText}>{document.name}</Text>}
                                description={
                                    <Caption1 block className={classes.cardCaption}>
                                        {document.size}
                                    </Caption1>
                                }
                            />
                            {isUploading
                                ? <ProgressBar className={classes.progressBarCustomStyles} shape="square" style={{ height: '6px' }} thickness="large" value={document.uploadPercentage} />
                                : <ProgressBar thickness="large" color={isSuccess ? 'success' : 'error'} style={{ height: '6px' }} shape="square" value={1} />}

                        </Card>
                        <span className={isUser ? classes.footer : mergeClasses(classes.footer, classes.floatLeft)}>
                            {isUploading ? 'File upload is in progress' : isSuccess ? 'Success: memory established' : 'Failed: memory not established'}
                        </span>
                        {/*<span className={classes.summarize}>
                        {document.isUploaded ? (
                            <button
                                className={classes.button}
                                style={{ transition: 'background-color 0.3s ease' }}
                                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                                onClick={() => handleSubmit(document.name)}
                            >
                                Summarize
                            </button>
                        ) : (
                            ''
                        )}
                    </span>*/}
                    </div>
                )
            })}
        </>
    );
};
